import { useFormData } from '@/composables/useFormData';
import Comment from '@/models/Comment';
import LumpSumCost from '@/models/LumpSumCost';
import LumpSumCostsService from '@/services/LumpSumCostsService';
import { defineStore } from 'pinia';
import { useRepo } from 'pinia-orm';

export const useLumpSumCostsStore = defineStore('lump_sum_costs', {
  state: () => ({
    costsForm: useFormData({
      costs: ([] as {
        current_claim: number;
        id: number;
        claim_id: number;
        trade_item_id: number;
        trade_name: string;
        trade_id: number;
      }[]),
    }),
  }),

  getters: {
    models: () => useRepo(LumpSumCost),
  },

  actions: {
    async create(
      claimId: Id,
      tradeItemId: Id,
      request: ApiRequests['api.v1.subcontractor.claims.lite.lump-sum.store'],
    ) {
      const [data, error] = await LumpSumCostsService.store(claimId, tradeItemId, request);

      if(error) {
        throw error;
      }

      this.models.save(data.cost);

      if(data.comment) {
        const comment = data.comment;
        const commentsRepo = useRepo(Comment);

        commentsRepo.save(comment);
      }

      return data;
    },

    async update(id: Id, request: ApiRequests['api.v1.lump-sum-costs.update']) {
      const [data, error] = await LumpSumCostsService.update(id, request);

      if(error) {
        throw error;
      }

      this.models.save(data.cost);

      if(data.comment) {
        const comment = data.comment;
        const commentsRepo = useRepo(Comment);

        commentsRepo.save(comment);
      }

      return data;
    },

    async storeComment(lumpSumCostId: Id, request: ApiRequests['api.v1.lump-sum-costs.comments.store']) {
      const [data, error] = await LumpSumCostsService.storeComment(lumpSumCostId, request);

      if(error) {
        throw error;
      }

      useRepo(Comment).save(data);

      return data;
    },

    async updateComment(
      lumpSumCostId: Id,
      commentId: Id,
      request: ApiRequests['api.v1.lump-sum-costs.comments.update'],
    ) {
      const [data, error] = await LumpSumCostsService.updateComment(lumpSumCostId, commentId, request);

      if(error) {
        throw error;
      }

      useRepo(Comment).save(data);

      return data;
    },

    async deleteComment(lumpSumCostId: Id, commentId: Id) {
      const [data, error] = await LumpSumCostsService.deleteComment(lumpSumCostId, commentId);

      if(error) {
        throw error;
      }

      useRepo(Comment).destroy(data.id);

      return data;
    },

    async delete(id: Id) {
      const [data, error] = await LumpSumCostsService.destroy(id);

      if(error) {
        throw error;
      }

      this.models.destroy(id);

      return data;
    },

    async saveClaimCost(
      claimId: Id,
      signedParams: unknown,
      request: ApiRequests['api.v1.subcontractor.claims.lump-sum.claim.store'],
    ) {
      const [data, error] = await LumpSumCostsService.subcontractor.store(claimId, signedParams, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      if(data.comment) {
        const comment = data.comment;
        const commentsRepo = useRepo(Comment);

        commentsRepo.save(comment);
      }

      return data;
    },

    async deleteLumpSumCost(
      claimId: Id,
      signedParams: unknown,
      request: ApiRequests['api.v1.subcontractor.claims.lump-sum.claim.destroy'],
    ) {
      const [data, error] = await LumpSumCostsService.subcontractor.destroy(claimId, signedParams, request);

      if(error) {
        throw error;
      }

      this.models.destroy(data.id);

      return data;
    },

    async saveCertifiedCost(
      projectId: Id,
      claimId: Id,
      id: Id,
      request: ApiRequests['api.v1.subcontractor.claims.lump-sum.certified.store'],
    ) {
      const [data, error] = await LumpSumCostsService.subcontractor.storeCertified(projectId, claimId, id, request);

      if(error) {
        throw error;
      }

      this.models.save(data.cost);

      if(data.comment) {
        const comment = data.comment;
        const commentsRepo = useRepo(Comment);

        commentsRepo.save(comment);
      }

      return data;
    },
  },
});
